/**
 * JobNotes - Editable notes textarea with auto-save on blur.
 */

import { useState, useCallback, useRef } from 'react';
import { usePipelineStore } from '@/store/pipeline.store';

interface JobNotesProps {
  jobId: string;
  initialNotes: string;
}

export function JobNotes({ jobId, initialNotes }: JobNotesProps) {
  const updateJobNotes = usePipelineStore((s) => s.updateJobNotes);
  const [notes, setNotes] = useState(initialNotes);
  const [savedMessage, setSavedMessage] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBlur = useCallback(() => {
    updateJobNotes(jobId, notes);
    setSavedMessage(true);

    // Clear saved message after 2 seconds
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setSavedMessage(false), 2000);
  }, [jobId, notes, updateJobNotes]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(e.target.value);
    setSavedMessage(false);
  }, []);

  return (
    <div className="job-notes">
      <div className="job-notes__header">
        <svg
          className="job-notes__icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
        <h3 className="job-notes__label">Notes</h3>
      </div>

      <textarea
        className="job-notes__textarea"
        value={notes}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Add your notes about this job... (e.g., interview prep, contact info, next steps)"
        aria-label="Job notes"
      />

      <div className="job-notes__footer">
        <span>{notes.length} characters</span>
        {savedMessage && <span className="job-notes__saved-msg">Saved</span>}
      </div>
    </div>
  );
}
